import React from "react";
import { motion } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useDashTheme } from "../../../hooks/useDashTheme";
import AntDropdown from "../../../../../components/UI/AntDropdown";
import { fmtDur, fmtClock } from "../bits";
import { Sk, dayPhrase, narrativeFor, stripGet } from "./storyBits";

/* Command header — same language as the graphical dashboard hero:
   a well with the day's number, then who / which day / the reading. */

const fmtDate = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

export function StoryHero({
  members, member, onMember, loadingMembers,
  date, onDate, data, loading, sf, refreshStrip,
  looks = [], onOpenLook,
  pickerOnly = false,
  overviewHours = null,
}) {
  const t = useDashTheme();
  const totals = data?.totals;

  const person = data?.person?.name
    || members.find((m) => m.value === member)?.label || "";
  const initials = person.split(" ").filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toUpperCase() || "·";

  const [y, m, d] = date.split("-").map(Number);
  const phrase = dayPhrase(totals, data?.threads, date);
  const narrative = narrativeFor(person, totals, data?.threads, data?.apps);
  const dateEyebrow = (() => {
    const dt = new Date(y, m - 1, d);
    const wd = dt.toLocaleDateString(undefined, { weekday: "long" });
    const mo = dt.toLocaleDateString(undefined, { month: "long" });
    return `${wd} · ${d} ${mo}`.toUpperCase();
  })();
  const awayMin = Math.round(totals?.awayMin || 0);
  const tracked = pickerOnly
    ? Math.round(overviewHours?.activeMin || 0)
    : Math.round(totals?.trackedMin || 0);
  const heroReady = pickerOnly ? overviewHours != null : !!totals;
  const trackedH = Math.floor(tracked / 60);
  const trackedM = String(tracked % 60).padStart(2, "0");

  const strip = React.useMemo(() => {
    const cache = member ? stripGet(member, data?.source || "both") : {};
    const out = [];
    for (let i = 13; i >= 0; i--) {
      const dt = new Date(); dt.setDate(dt.getDate() - i);
      const key = fmtDate(dt);
      out.push({
        key,
        lbl: dt.toLocaleDateString(undefined, { weekday: "short" }),
        num: dt.getDate(),
        min: cache[key],
      });
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [member, data?.source, refreshStrip]);
  const stripMax = Math.max(60, ...strip.map((s) => s.min || 0));

  const shiftDay = (delta) => {
    const next = new Date(y, m - 1, d + delta);
    if (next > new Date()) return;
    onDate(fmtDate(next));
  };
  const isToday = date === fmtDate(new Date());
  const verdictTone = looks.length ? t.status.warning : t.status.positive;

  return (
    <div style={{
      display: "grid",
      gridTemplateColumns: "minmax(240px, 300px) 1fr",
      borderRadius: t.r.hero,
      background: t.c.surface,
      border: `1px solid ${sf.cardBorder}`,
      boxShadow: t.shadow.raised,
      overflow: "hidden",
      marginBottom: t.s[4],
    }} className="st-hero-band">
      <div style={{
        padding: "26px 24px 22px",
        background: t.isDark ? t.c.surfaceMuted : "#F1F3F7",
        borderRight: `1px solid ${sf.hairline}`,
        display: "flex", flexDirection: "column",
      }}>
        <div style={{ ...t.font.statLabel, color: t.c.primary }}>Tracked</div>
        {!pickerOnly && (loading || !member) ? (
          <div style={{ marginTop: 14 }}>
            <Sk w={140} h={56} r={12} />
            <Sk w={110} h={12} style={{ marginTop: 14 }} />
          </div>
        ) : (
          <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, ease: t.motion.ease.out }}>
            <div style={{
              fontSize: 56, fontWeight: 680, letterSpacing: "-2.4px", lineHeight: 1,
              margin: "10px 0 12px", fontVariantNumeric: "tabular-nums",
            }}>
              {!heroReady ? (
                <span style={{ fontSize: 28, fontWeight: 600, color: t.c.textTertiary }}>—</span>
              ) : trackedH > 0 ? (
                <>
                  {trackedH}h
                  <span style={{ fontSize: 26, fontWeight: 640, letterSpacing: "-1px", marginLeft: 6 }}>
                    {trackedM}m
                  </span>
                </>
              ) : <>{tracked}m</>}
            </div>
            {!pickerOnly && data?.bounds && (
              <div style={{ fontSize: 13, color: t.c.textSecondary, fontVariantNumeric: "tabular-nums" }}>
                {fmtClock(data.bounds.startMs)} → {fmtClock(data.bounds.endMs)}
              </div>
            )}
            {pickerOnly && overviewHours && (
              <div style={{ fontSize: 13, color: t.c.textSecondary, fontVariantNumeric: "tabular-nums" }}>
                {overviewHours.firstStart} → {overviewHours.lastSeen}
              </div>
            )}
            {(heroReady || (!pickerOnly && totals)) && (
              <div style={{
                marginTop: "auto", paddingTop: 16,
                display: "inline-flex", alignItems: "center", gap: 8,
                fontSize: 12.5, fontWeight: 600,
                color: verdictTone,
              }}>
                <i style={{ width: 8, height: 8, borderRadius: 99, background: verdictTone, boxShadow: `0 0 0 4px ${verdictTone}2a` }} />
                {pickerOnly
                  ? (overviewHours ? "From overview" : "Generate to see hours")
                  : looks.length
                    ? `${looks.length} worth a look`
                    : tracked ? "Settled day" : "Nothing tracked"}
              </div>
            )}
          </motion.div>
        )}
      </div>

      <div style={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
        <div className="st-hero-in" style={{ display: "flex", alignItems: "flex-start", gap: 18, padding: "22px 22px 8px" }}>
          <span style={{
            width: 44, height: 44, borderRadius: 14, flex: "none",
            background: `linear-gradient(135deg, ${t.c.ai}, ${t.c.primary})`, color: "#fff",
            display: "grid", placeItems: "center", fontWeight: 700, fontSize: 15, letterSpacing: "0.02em",
          }}>{initials}</span>

          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
              <div style={{ minWidth: 210 }}>
                <AntDropdown
                  value={member || undefined} options={members} onChange={(v) => onMember(v || null)}
                  placeholder={loadingMembers ? "Loading…" : `Choose someone (${members.length})`}
                />
              </div>
              {data?.person?.position && (
                <span style={{ fontSize: t.font.size.sm, color: t.c.textTertiary }}>{data.person.position}</span>
              )}
            </div>

            {pickerOnly ? (
              <div style={{ marginTop: 12, fontSize: t.font.size.base, color: t.c.textSecondary, maxWidth: 640 }}>
                Pick a person and day, then generate the client overview below. Hours here match that report — they are not loaded from The day.
              </div>
            ) : loading || !member ? (
              <div style={{ marginTop: 14 }}>
                <Sk w={280} h={22} style={{ marginBottom: 10 }} />
                <Sk w="78%" h={13} />
              </div>
            ) : (
              <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, ease: t.motion.ease.out }}>
                <div style={{ ...t.font.microLabel, color: t.c.textTertiary, letterSpacing: "0.13em", marginTop: 12 }}>
                  {dateEyebrow}
                </div>
                <div style={{ marginTop: 6, fontSize: 22, fontWeight: t.font.weight.semibold, letterSpacing: "-0.028em", lineHeight: 1.25, color: t.c.text, textTransform: "capitalize" }}>
                  {phrase}
                </div>
                <div style={{ marginTop: 8, fontSize: t.font.size.base, lineHeight: 1.55, color: t.c.textSecondary, maxWidth: 720 }}>
                  {narrative}
                </div>
                {looks.length > 0 && (
                  <div style={{ marginTop: 10, fontSize: 13, color: t.c.textTertiary, lineHeight: 1.5 }}>
                    Open{" "}
                    {looks.map((th, i) => (
                      <React.Fragment key={th.key}>
                        {i > 0 && ", "}
                        <button
                          type="button" className="st-reset st-focusable st-textlink"
                          onClick={() => onOpenLook?.(th)}
                          style={{ color: t.c.primary, fontWeight: 600, cursor: "pointer" }}
                        >{th.title}</button>
                      </React.Fragment>
                    ))}
                  </div>
                )}
                {totals && (
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 14 }}>
                    <HeroChip dot={t.status.positive}>
                      {fmtDur(totals.trackedMin)} tracked{awayMin ? ` · ${fmtDur(awayMin)} away` : ""}
                    </HeroChip>
                    <HeroChip>{totals.threadCount} threads · {data?.apps?.length || 0} apps</HeroChip>
                  </div>
                )}
              </motion.div>
            )}
          </div>
        </div>

        <div className="st-scrub" style={{
          display: "flex", alignItems: "flex-end", gap: 5,
          padding: `12px 20px 14px`,
          marginTop: "auto",
          borderTop: `1px solid ${sf.hairline}`,
        }}>
          <ArrowBtn onClick={() => shiftDay(-1)} label="Previous day"><ChevronLeft size={14} /></ArrowBtn>
          {strip.map((s) => {
            const sel = s.key === date;
            const future = s.key > fmtDate(new Date());
            const known = s.min != null;
            const h = known ? 8 + (s.min / stripMax) * 28 : 8;
            return (
              <button
                key={s.key} type="button" disabled={future}
                className="st-reset st-scrubday st-focusable"
                onClick={() => !sel && onDate(s.key)}
                title={known ? `${s.lbl} ${s.num} · ${fmtDur(s.min)} tracked` : `${s.lbl} ${s.num} · open to load`}
                style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 5, cursor: sel ? "default" : "pointer", minWidth: 26 }}
              >
                <span className="st-scrubbar" style={{
                  width: "100%", maxWidth: 26, height: h, borderRadius: "5px 5px 2px 2px",
                  background: sel ? t.c.primary
                    : known ? `${t.c.primary}${t.isDark ? "59" : "3D"}`
                    : (t.isDark ? "rgba(255,255,255,.09)" : "rgba(24,32,46,.09)"),
                  boxShadow: sel ? `0 0 0 3px ${t.c.primary}30` : "none",
                }} />
                <span style={{
                  fontSize: 10, fontWeight: sel ? 700 : 600, letterSpacing: "0.03em",
                  color: sel ? t.c.primary : t.c.textTertiary, whiteSpace: "nowrap",
                }}>{s.lbl} {s.num}</span>
              </button>
            );
          })}
          <ArrowBtn onClick={() => shiftDay(1)} label="Next day" disabled={isToday}><ChevronRight size={14} /></ArrowBtn>
        </div>
      </div>
    </div>
  );
}

function ArrowBtn({ children, onClick, label, disabled }) {
  const t = useDashTheme();
  return (
    <button
      type="button" onClick={onClick} aria-label={label} disabled={disabled}
      className="st-reset st-focusable st-hit-icon"
      style={{
        width: 28, height: 28, borderRadius: 8, flex: "none", alignSelf: "center",
        display: "grid", placeItems: "center", border: `1px solid ${t.c.border}`,
        color: disabled ? t.c.textTertiary : t.c.textSecondary,
        cursor: disabled ? "not-allowed" : "pointer", opacity: disabled ? 0.45 : 1,
        marginBottom: 14, background: t.c.surface,
      }}
    >{children}</button>
  );
}

function HeroChip({ children, dot, tone }) {
  const t = useDashTheme();
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 8, whiteSpace: "nowrap",
      fontSize: t.font.size.sm, fontWeight: t.font.weight.medium,
      color: tone || t.c.textSecondary,
      background: tone ? `${tone}1f` : (t.isDark ? t.c.surfaceMuted : t.c.surfaceHover),
      border: `1px solid ${tone ? `${tone}45` : t.c.border}`,
      borderRadius: t.r.pill, padding: "5px 12px",
      fontVariantNumeric: "tabular-nums",
    }}>
      {dot && <i style={{ width: 7, height: 7, borderRadius: "50%", background: dot }} />}
      {children}
    </span>
  );
}
